import React, { useMemo, useState } from "react"
import { Box, Typography, Chip, Tooltip, IconButton } from "@mui/material"
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import TodayIcon from '@mui/icons-material/Today'
import { MODALITY_OPTIONS } from "../../../../constants/Consult"
import { getCaseStatusColor } from "../../Schedule/caseStatusColor"

const HOUR_HEIGHT = 48
const START_HOUR = 6
const END_HOUR = 22
const HOURS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i)

const timeToMinutes = (t) => {
    const [h, m] = t.split(':').map(Number)
    return h * 60 + m
}

const formatTime12 = (t) => {
    const [h, m] = t.split(':').map(Number)
    const period = h >= 12 ? 'pm' : 'am'
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
    return m === 0 ? `${h12}${period}` : `${h12}:${String(m).padStart(2, '0')}${period}`
}

const layoutSlots = (slots) => {
    if (!slots.length) return []
    const items = slots.map((slot, i) => ({
        index: i, start: timeToMinutes(slot.startTime), end: timeToMinutes(slot.endTime), col: 0, totalCols: 1
    })).sort((a, b) => a.start - b.start || a.end - b.end)
    const columns = []
    for (const item of items) {
        let placed = false
        for (let c = 0; c < columns.length; c++) {
            if (item.start >= columns[c]) { item.col = c; columns[c] = item.end; placed = true; break }
        }
        if (!placed) { item.col = columns.length; columns.push(item.end) }
    }
    for (const item of items) {
        const maxCol = Math.max(...items.filter(o => o.start < item.end && o.end > item.start).map(o => o.col)) + 1
        item.totalCols = maxCol
    }
    const layout = new Array(slots.length)
    for (const item of items) layout[item.index] = { col: item.col, totalCols: item.totalCols }
    return layout
}

const formatDate = (dateStr) =>
    new Date(dateStr + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'numeric', day: 'numeric' })

const ModalityGrid = ({ date, slots, limits, countByModality, visibleModalities, onDateChange, onDrop, onSlotClick, onHeaderClick, editable }) => {
    const [dragOver, setDragOver] = useState(null) // { modality, snapTop }
    const gridHeight = HOURS.length * HOUR_HEIGHT
    const totalMinutes = (END_HOUR - START_HOUR) * 60
    const today = new Date().toISOString().split('T')[0]
    const columns = visibleModalities?.length ? visibleModalities : MODALITY_OPTIONS

    const calcSnapTop = (clientY, rect) => {
        const y = clientY - rect.top
        const mins = Math.floor(y / gridHeight * totalMinutes) + START_HOUR * 60
        const snapped = Math.floor(mins / 30) * 30
        return ((snapped - START_HOUR * 60) / totalMinutes) * gridHeight
    }

    const slotsByModality = useMemo(() => {
        const map = {}
        for (const mod of columns) map[mod] = []
        for (const slot of (slots || [])) {
            if (map[slot.modality]) map[slot.modality].push(slot)
        }
        return map
    }, [slots, columns])

    const handleColumnDrop = (e, modality) => {
        e.preventDefault()
        setDragOver(null)
        try {
            const data = JSON.parse(e.dataTransfer.getData('text/plain'))
            if (!data.studyId) return
            const rect = e.currentTarget.getBoundingClientRect()
            const y = e.clientY - rect.top
            const mins = Math.floor(y / gridHeight * totalMinutes) + START_HOUR * 60
            const snapped = Math.floor(mins / 30) * 30
            const h = Math.floor(snapped / 60)
            const m = snapped % 60
            const startTime = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
            const endMins = snapped + 30
            const endTime = `${String(Math.floor(endMins / 60)).padStart(2, '0')}:${String(endMins % 60).padStart(2, '0')}`
            onDrop?.(data.studyId, modality, startTime, endTime)
        } catch (_) {}
    }

    return (
        <Box>
            {/* Navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                <IconButton size="small" onClick={() => {
                    const d = new Date(date + 'T12:00:00'); d.setDate(d.getDate() - 1); onDateChange(d.toISOString().split('T')[0])
                }}><ChevronLeftIcon /></IconButton>
                <IconButton size="small" onClick={() => onDateChange(today)}><TodayIcon /></IconButton>
                <IconButton size="small" onClick={() => {
                    const d = new Date(date + 'T12:00:00'); d.setDate(d.getDate() + 1); onDateChange(d.toISOString().split('T')[0])
                }}><ChevronRightIcon /></IconButton>
                <Typography variant="h6" sx={{ ml: 1 }}>{formatDate(date)}</Typography>
            </Box>

            <Box sx={{ display: 'flex', overflow: 'auto' }}>
                {/* Time gutter */}
                <Box sx={{ width: 50, flexShrink: 0 }}>
                    <Box sx={{ height: 52, borderBottom: 1, borderColor: 'divider' }} />
                    <Box sx={{ position: 'relative', height: gridHeight }}>
                        {HOURS.map(h => (
                            <Typography key={h} variant="caption" sx={{
                                position: 'absolute', top: (h - START_HOUR) * HOUR_HEIGHT - 8, right: 4,
                                color: 'text.secondary', fontSize: 10, userSelect: 'none'
                            }}>
                                {h === 0 ? '12am' : h < 12 ? `${h}am` : h === 12 ? '12pm' : `${h - 12}pm`}
                            </Typography>
                        ))}
                    </Box>
                </Box>

                {/* Modality columns */}
                <Box sx={{ display: 'flex', flex: 1, minWidth: 0 }}>
                    {columns.map((modality, mi) => {
                        const colSlots = slotsByModality[modality] || []
                        const slotLayout = layoutSlots(colSlots)
                        const limit = limits?.[modality]
                        const count = countByModality?.[modality] || 0
                        const isFull = limit?.max != null && count >= limit.max

                        return (
                            <Box key={modality} sx={{ flex: 1, minWidth: 80, borderRight: mi < columns.length - 1 ? 1 : 0, borderColor: 'divider' }}>
                                {/* Column header */}
                                <Box
                                    onClick={() => onHeaderClick?.(modality, limit, count)}
                                    sx={{
                                        height: 52, display: 'flex', flexDirection: 'column', alignItems: 'center',
                                        justifyContent: 'center', borderBottom: 1, borderColor: 'divider',
                                        cursor: editable ? 'pointer' : 'default',
                                        '&:hover': editable ? { bgcolor: 'action.hover' } : {}
                                    }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 11 }}>{modality}</Typography>
                                    {limit?.max != null && (
                                        <Chip size="small" label={`${count}/${limit.max}`}
                                            color={isFull ? 'error' : 'default'}
                                            sx={{ fontSize: 9, height: 14, mt: '2px' }} />
                                    )}
                                </Box>

                                {/* Time grid */}
                                <Box
                                    onDragOver={onDrop ? (e) => { e.preventDefault(); const rect = e.currentTarget.getBoundingClientRect(); const clientY = e.clientY; setDragOver({ modality, snapTop: calcSnapTop(clientY, rect) }) } : undefined}
                                    onDragLeave={onDrop ? (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(null) } : undefined}
                                    onDrop={onDrop ? (e) => handleColumnDrop(e, modality) : undefined}
                                    sx={{
                                        position: 'relative', height: gridHeight,
                                        bgcolor: dragOver?.modality === modality ? 'action.hover' : 'transparent',
                                        transition: 'background-color 0.15s'
                                    }}>
                                    {HOURS.map(h => (
                                        <Box key={h} sx={{ position: 'absolute', top: (h - START_HOUR) * HOUR_HEIGHT, left: 0, right: 0, borderTop: 1, borderColor: 'divider', pointerEvents: 'none' }} />
                                    ))}
                                    {dragOver?.modality === modality && (
                                        <Box sx={{
                                            position: 'absolute', left: 0, right: 0,
                                            top: dragOver.snapTop - 1,
                                            height: 2, bgcolor: 'primary.main',
                                            pointerEvents: 'none', zIndex: 10,
                                            boxShadow: '0 0 4px rgba(25,118,210,0.6)'
                                        }} />
                                    )}

                                    {colSlots.map((slot, si) => {
                                        const startMin = timeToMinutes(slot.startTime) - START_HOUR * 60
                                        const endMin = timeToMinutes(slot.endTime) - START_HOUR * 60
                                        const top = (startMin / totalMinutes) * gridHeight
                                        const height = Math.max(((endMin - startMin) / totalMinutes) * gridHeight, 20)
                                        const { col, totalCols } = slotLayout[si] || { col: 0, totalCols: 1 }
                                        const study = slot.studyId
                                        const name = study?.PatientMainDicomTags?.PatientName || 'Unknown'
                                        const bgcolor = getCaseStatusColor(study)

                                        return (
                                            <Tooltip key={slot.id} title={`${formatTime12(slot.startTime)}–${formatTime12(slot.endTime)}`} arrow placement="top">
                                                <Box
                                                    draggable
                                                    onDragStart={e => {
                                                        e.stopPropagation()
                                                        e.dataTransfer.setData('text/plain', JSON.stringify({ studyId: String(study?.id || study?._id || slot.studyId) }))
                                                    }}
                                                    onClick={() => onSlotClick?.(slot)}
                                                    sx={{
                                                        position: 'absolute', top, height,
                                                        width: `${100 / totalCols}%`,
                                                        left: `${(col / totalCols) * 100}%`,
                                                        bgcolor, borderRadius: 1, p: '3px 5px',
                                                        overflow: 'hidden', cursor: 'pointer', opacity: 0.9,
                                                        '&:hover': { opacity: 1 },
                                                        boxSizing: 'border-box',
                                                        border: '1px solid rgba(255,255,255,0.2)',
                                                        transition: 'opacity 0.15s'
                                                    }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#fff', fontSize: 10, lineHeight: 1.2, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {name}
                                                    </Typography>
                                                    {height > 28 && (
                                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 9, lineHeight: 1.2, display: 'block' }}>
                                                            {formatTime12(slot.startTime)}–{formatTime12(slot.endTime)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Tooltip>
                                        )
                                    })}
                                </Box>
                            </Box>
                        )
                    })}
                </Box>
            </Box>
        </Box>
    )
}

export default ModalityGrid
